import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";

import { safeOrderSummaryTrace, buildLdsSuccessTotalDebugDedup } from "../../../../utils/safeOrderSummaryTrace";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { getHotelInfo, getLdsOrder, loadAirlines } from "../../../../services/fhpService";
import translateLDSDestinations from "../../../../services/translateLDSDestinations";
import { get_depret_from_result } from "../../../../utils/LDSHelpers";
import { resolveCustomerQuoteUsdAndOfferFromOrder } from "../../../../utils/customerQuoteDisplay";
import { mergeLdsSelectedResultForOfferDisplay } from "../../../../utils/resolveCustomerFacingOfferNumber";
import {
  deriveLdsOrderBusinessStatus,
  isBankTransferAwaitingPayment,
  isLdsOrderBookingConfirmed,
  isLdsProposalEntity,
} from "../../../../utils/ldsOrderStatus";
import { Alert, message } from "antd";

import { Loader } from "../../../../Components/Loader/Loader";
import { shortClockFromLdsDateTime } from "../../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import OrderSummary from "../OrderSummary";
import PackageProfileAttractionsUnifiedBlock from "../../../PackageAttractionsProfileSection/PackageProfileAttractionsUnifiedBlock";
import { resolveProposalCustomerNameForOffer } from "../../../../utils/proposalCustomerName";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { resolvePersistedPublicOfferPackageIls } from "../../../../utils/packageProfilePublicOfferPrice";
import { computePublicPackagesProfileSuccessPayableTotal } from "../../../../utils/computePublicPackagesProfileSuccessPayableTotal";
import { buildCustomerQuoteAgencyBranding } from "../../../../utils/buildCustomerQuoteAgencyBranding";
import { resolveCustomerQuoteAdditionalInfoHtml } from "../../../../utils/resolveCustomerQuoteAdditionalInfoHtml";
import { decodeLdsDealPayload } from "../../../../utils/orders/decodeLdsDealPayload";
import { executeLdsCreditPaymentLink } from "../../../../utils/executeLdsCreditPaymentLink";
import { resolveBoardBasisForPackageSummary } from "../../../../utils/resolveBoardBasisForPackageSummary";
import { resolveOrderPaymentStatusLabel } from "../../../../utils/resolveOrderPaymentStatusLabel";
import { resolveBaggageSummary } from "../../../../utils/resolveBaggageSummary";
import {
  orderDealtypeIsOrganizedTour,
  buildOrganizedTourPackageSuccessViewModel,
  resolveOrganizedTourSuccessHeroImage,
  ORG_TOUR_BANK_TRANSFER_SUCCESS_SUBTITLE_HE,
} from "../../../../utils/organizedTourPackageSuccessViewModel";
import { useDeepCompareMemo } from "../../../../hooks/useDeepCompareMemo";
import { buildPublicOfferIncludedBaggageVm } from "../../../../utils/publicOfferIncludedBaggageDisplay";
import { buildPublicOfferIncludedTransfersVm } from "../../../../utils/publicOfferIncludedTransfersDisplay";
import { logPublicOfferContinueFlowTrace } from "../../../../utils/publicOfferContinueFlowTrace";

const DEBUG_SUMMARY = process.env.NODE_ENV === "development";

function buildPassengersList(order) {
  if (!order || typeof order !== "object") return [];
  const list = [];
  const seen = new Set();

  const norm = (v) => String(v || "").trim();
  const detectType = (person, fallback = "adult") => {
    const raw =
      norm(person?.type) ||
      norm(person?.paxType) ||
      norm(person?.passengerType) ||
      norm(person?.category) ||
      "";
    const upper = raw.toUpperCase();
    if (
      upper.includes("CHD") ||
      upper.includes("CHILD") ||
      upper.includes("KID") ||
      upper.includes("ילד")
    ) {
      return "child";
    }
    if (
      upper.includes("INF") ||
      upper.includes("INFANT") ||
      upper.includes("תינוק")
    ) {
      return "child";
    }
    return fallback;
  };

  const pushPerson = (person, fallbackType = "adult") => {
    if (!person || typeof person !== "object") return;
    const title =
      norm(person?.paxTitle) || norm(person?.title) || norm(person?.namePrefix);
    const first =
      norm(person?.firstName) ||
      norm(person?.firstname) ||
      norm(person?.first_name) ||
      norm(person?.givenName);
    const last =
      norm(person?.lastName) ||
      norm(person?.lastname) ||
      norm(person?.last_name) ||
      norm(person?.surname) ||
      norm(person?.familyName);
    const full = [title, first, last].filter(Boolean).join(" ").trim();
    if (!full) return;
    const key = full.replace(/\s+/g, " ").toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push({ name: full, type: detectType(person, fallbackType) });
  };

  const paxRooms =
    order?.passengersinfo ?? order?.passengers_info ?? order?.passengersInfo ?? [];
  if (Array.isArray(paxRooms)) {
    paxRooms.forEach((room) => {
      if (!room || typeof room !== "object") return;
      (Array.isArray(room?.adults) ? room.adults : []).forEach((p) =>
        pushPerson(p, "adult")
      );
      (Array.isArray(room?.kids) ? room.kids : []).forEach((p) =>
        pushPerson(p, "child")
      );
      (Array.isArray(room?.infants) ? room.infants : []).forEach((p) =>
        pushPerson(p, "child")
      );
      // Some payloads keep passengers directly at room level.
      pushPerson(room, "adult");
    });
  }

  const reservationPax = order?.order_response?.reservation?.pax;
  if (Array.isArray(reservationPax)) {
    reservationPax.forEach((p) => pushPerson(p, "adult"));
  } else if (reservationPax && typeof reservationPax === "object") {
    pushPerson(reservationPax, "adult");
  }

  const flatPassengers = order?.passengers ?? order?.passengerList ?? order?.pax ?? [];
  if (Array.isArray(flatPassengers)) {
    flatPassengers.forEach((p) => pushPerson(p, "adult"));
  }

  return list;
}

const ROOM_NAME_FALLBACK = "חדר סטנדרטי";

/** Normalize USD price from API (number or string) to number or null */
function toUsdNumber(value) {
  if (value == null) return null;
  const n = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : Number(value);
  return typeof n === "number" && !Number.isNaN(n) && n > 0 ? n : null;
}

/** שם חדר משורת נוסעים: API לעיתים שומר roomName ברמה העליה, לעיתים רק room.roomType (בחירת סוכן). */
function roomDisplayNameFromPaxEntry(entry) {
  if (!entry || typeof entry !== "object") return "";
  const flat = entry.roomName ?? entry.room_name ?? entry.roomType;
  if (flat != null && String(flat).trim()) return String(flat).trim();
  const nested = entry.room;
  if (nested && typeof nested === "object") {
    const nt = nested.roomType ?? nested.ROOM_TYPE ?? nested.room_type;
    if (nt != null && String(nt).trim()) return String(nt).trim();
  }
  return "";
}

/** רשימת שמות חדרים — passengers_info (מסד) או passengersinfo (LDS). */
function buildRoomNamesList(order, singleRoomType) {
  const list =
    order?.passengers_info ?? order?.passengersinfo ?? order?.passengersInfo;
  if (!list || !Array.isArray(list) || list.length === 0) {
    return singleRoomType ? [singleRoomType || ROOM_NAME_FALLBACK] : [ROOM_NAME_FALLBACK];
  }
  const singleFallback =
    list.length === 1 && singleRoomType && String(singleRoomType).trim()
      ? String(singleRoomType).trim()
      : null;
  return list.map((entry) => {
    const name = roomDisplayNameFromPaxEntry(entry);
    if (name) return name;
    return singleFallback || ROOM_NAME_FALLBACK;
  });
}

function absolutizeImageUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    try {
      const protocol =
        typeof window !== "undefined" && window.location?.protocol
          ? window.location.protocol
          : "https:";
      return `${protocol}${trimmed}`;
    } catch {
      return `https:${trimmed}`;
    }
  }
  if (trimmed.startsWith("/")) {
    try {
      if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}${trimmed}`;
      }
    } catch {
      /* ignore */
    }
  }
  return trimmed;
}

function extractFirstImageCandidate(input) {
  if (!input || typeof input !== "object") return null;
  const direct = [
    input.image,
    input.imageUrl,
    input.image_url,
    input.hotelImage,
    input.hotel_image,
    input.mainImage,
    input.main_image,
    input.thumbnail,
    input.resourceUrl,
    input.resource_url,
    input.url,
    input.img,
  ];
  for (const candidate of direct) {
    const abs = absolutizeImageUrl(candidate);
    if (abs) return abs;
  }
  const arrays = [
    input.images,
    input.hotelImages,
    input.pictures,
    input.gallery,
    input.photos,
  ];
  for (const arr of arrays) {
    if (!Array.isArray(arr) || arr.length === 0) continue;
    for (const item of arr) {
      if (typeof item === "string") {
        const abs = absolutizeImageUrl(item);
        if (abs) return abs;
        continue;
      }
      if (item && typeof item === "object") {
        const abs = extractFirstImageCandidate(item);
        if (abs) return abs;
      }
    }
  }
  if (input.hotel && typeof input.hotel === "object") {
    const nested = extractFirstImageCandidate(input.hotel);
    if (nested) return nested;
  }
  if (input.hotelInfo && typeof input.hotelInfo === "object") {
    const nested = extractFirstImageCandidate(input.hotelInfo);
    if (nested) return nested;
  }
  if (Array.isArray(input.hotelInfo) && input.hotelInfo[0]) {
    const nested = extractFirstImageCandidate(input.hotelInfo[0]);
    if (nested) return nested;
  }
  if (input.normalized && typeof input.normalized === "object") {
    const nested = extractFirstImageCandidate(input.normalized);
    if (nested) return nested;
  }
  return null;
}

function resolveHotelImageFromProfileData(orderData, livePackageData) {
  const fromLive = extractFirstImageCandidate(livePackageData);
  if (fromLive) {
    return { url: fromLive, source: "livePackage" };
  }
  const fromOrder = extractFirstImageCandidate(orderData);
  if (fromOrder) {
    return { url: fromOrder, source: "orderData" };
  }
  return { url: null, source: "missing" };
}

function decodeHotelSearchHintFromOrder(order) {
  if (!order || typeof order !== "object") return null;
  const candidates = [
    order?.linkedflights?.ldsDealId,
    order?.linkedFlights?.ldsDealId,
    order?.passengersinfo?.[0]?.room?.ldsDealId,
    order?.passengersinfo?.[0]?.ldsDealId,
    order?.extended?.[0]?.ldsDealId,
  ];
  for (const encoded of candidates) {
    const decoded = decodeLdsDealPayload(encoded);
    if (!decoded) continue;
    const hotel_name =
      decoded.hotelName && String(decoded.hotelName).trim()
        ? String(decoded.hotelName).trim()
        : "";
    const city_code =
      decoded.hotelCity && String(decoded.hotelCity).trim()
        ? String(decoded.hotelCity).trim()
        : "";
    if (!hotel_name && !city_code) continue;
    return {
      hotel_name: hotel_name || null,
      city_code: city_code || null,
      hotel_id: decoded.hotelId ?? null,
    };
  }
  return null;
}

function pickFirstUsablePaymentUrl(order) {
  if (!order || typeof order !== "object") return null;
  const payments = Array.isArray(order?.payments) ? order.payments : [];
  for (const row of payments) {
    if (row?.paid === true) continue;
    const raw = row?.paymentUrl ?? row?.url ?? row?.payment_url;
    if (raw != null && String(raw).trim() !== "") {
      return String(raw).trim();
    }
  }
  const links = Array.isArray(order?.payment_links) ? order.payment_links : [];
  for (const row of links) {
    if (row?.paid === true) continue;
    const raw = row?.paymentUrl ?? row?.url ?? row?.payment_url;
    if (raw != null && String(raw).trim() !== "") {
      return String(raw).trim();
    }
  }
  if (order?.payment_link != null && String(order.payment_link).trim() !== "") {
    return String(order.payment_link).trim();
  }
  return null;
}

/** קוד IATA ללוגו Airhex — 2–3 תווים */
function normalizeCarrierIata(carrier) {
  const s = String(carrier || "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2,3}$/.test(s)) return s;
  const letters = s.replace(/[^A-Z]/g, "");
  if (letters.length >= 2) return letters.slice(0, 3);
  const alnum = s.replace(/[^A-Z0-9]/g, "");
  return alnum.length >= 2 ? alnum.slice(0, 3) : s.slice(0, 3);
}

/** ממיפה קוד carrier לשם ולוגו של חברת תעופה */
function enrichFlightWithAirlineInfo(flightObj, airlinesMap) {
  if (!flightObj || !flightObj.carrier || !airlinesMap) return flightObj;

  const airline = airlinesMap[flightObj.carrier] || airlinesMap[normalizeCarrierIata(flightObj.carrier)];
  if (airline) {
    return {
      ...flightObj,
      airlineName: flightObj.airlineName || airline.name || "",
      airlineLogo: flightObj.airlineLogo || airline.logo || "",
    };
  }
  return flightObj;
}

/**
 * LDS לעיתים שולח שעה רק בתוך depDate/arrDate ("DD/MM/YYYY HH:mm" או ISO), בלי depTime/arrTime נפרדים.
 */
function normalizeSegmentClocks(depDate, depTime, arrDate, arrTime) {
  let d = depTime != null && String(depTime).trim() ? String(depTime).trim() : "";
  let a = arrTime != null && String(arrTime).trim() ? String(arrTime).trim() : "";
  if (!d && depDate) {
    const c = shortClockFromLdsDateTime(depDate);
    if (c) d = c;
  }
  if (!a && arrDate) {
    const c = shortClockFromLdsDateTime(arrDate);
    if (c) a = c;
  }
  return { depTime: d, arrTime: a };
}

/** מחלץ פרטי טיסה מה-order ומחזיר מערך של אובייקטים עם פרטי טיסה */
function extractFlightDetails(order, selectedResult, airlinesMap = null) {
  // נסה למצוא פרטי טיסה ממקורות שונים
  const reservation = order?.order_response?.reservation;
  const segments = reservation?.segments || order?.segments || order?.order_response?.segments;
  
  // נסה גם מ-selectedResult
  const linkedFlights = selectedResult?.linkedFlights;
  const flightFromResult = selectedResult?.flight;
  
  // אם יש segments במערך
  if (Array.isArray(segments) && segments.length > 0) {
    const flightObjects = segments.map((seg, idx) => {
      if (typeof seg === 'string') {
        // אם זה ID, נסה למצוא את הפרטים במקום אחר
        return null;
      }
      const carrier =
        seg?.carrier ||
        seg?.carrierCode ||
        seg?.airlineId ||
        seg?.airlineCode ||
        seg?.airline ||
        "";
      const flightNum = seg?.flightNumber || seg?.flight_number || "";
      const depDate = seg?.depDate || seg?.departureDate || seg?.dep_date || seg?.departure?.date || "";
      const arrDate = seg?.arrDate || seg?.arrivalDate || seg?.arr_date || seg?.arrival?.date || "";
      let depTime =
        seg?.depTime ||
        seg?.departureTime ||
        seg?.dep_time ||
        (seg?.departure?.time != null ? String(seg.departure.time) : "") ||
        "";
      let arrTime =
        seg?.arrTime ||
        seg?.arrivalTime ||
        seg?.arr_time ||
        (seg?.arrival?.time != null ? String(seg.arrival.time) : "") ||
        "";
      ({ depTime, arrTime } = normalizeSegmentClocks(depDate, depTime, arrDate, arrTime));
      const depAirport = seg?.depAirport || seg?.departureAirport || seg?.origin || "";
      const arrAirport = seg?.arrAirport || seg?.arrivalAirport || seg?.destination || "";
      const airlineName = seg?.airlineName || seg?.carrierName || seg?.airline_name || '';
      const airlineLogo = seg?.airlineLogo || seg?.carrierLogo || seg?.logo || '';
      const supplierCode = seg?.supplierCode || seg?.supplier || seg?.gds || '';

      const flightCode = carrier && flightNum ? `${carrier}${flightNum}` : (flightNum || carrier || '');
      const route = depAirport && arrAirport ? `${depAirport}-${arrAirport}` : '';
      const dateTime = depDate && depTime ? `${depTime} ${depDate}` : depDate || '';
      
      if (!flightCode && !dateTime) return null;
      
      const flightObj = {
        carrier,
        flightNumber: flightNum,
        flightCode,
        depDate,
        depTime,
        arrTime,
        arrDate,
        dateTime,
        route,
        depCode: depAirport ? String(depAirport).trim() : "",
        arrCode: arrAirport ? String(arrAirport).trim() : "",
        airlineName,
        airlineLogo,
        supplierCode: supplierCode ? String(supplierCode).trim() : "",
      };

      return enrichFlightWithAirlineInfo(flightObj, airlinesMap);
    }).filter(Boolean);
    
    if (flightObjects.length > 0) {
      return flightObjects;
    }
  }
  
  // נסה מ-linkedFlights
  if (linkedFlights?.flight) {
    const flights = Array.isArray(linkedFlights.flight) ? linkedFlights.flight : [linkedFlights.flight];
    const flightObjects = flights.map((f) => {
      const leg = f?.leg || f;
      const carrier =
        leg?.carrier ||
        leg?.carrierCode ||
        leg?.airlineId ||
        leg?.airlineCode ||
        "";
      const flightNum = leg?.flightNumber || leg?.flight_number || "";
      const depDate = leg?.depDate || leg?.departureDate || leg?.departure?.date || "";
      const arrDate = leg?.arrDate || leg?.arrivalDate || leg?.arrival?.date || "";
      let depTime =
        leg?.depTime ||
        leg?.departureTime ||
        (leg?.departure?.time != null ? String(leg.departure.time) : "") ||
        "";
      let arrTime =
        leg?.arrTime ||
        leg?.arrivalTime ||
        (leg?.arrival?.time != null ? String(leg.arrival.time) : "") ||
        "";
      ({ depTime, arrTime } = normalizeSegmentClocks(depDate, depTime, arrDate, arrTime));
      const depAirport = leg?.depAirport || leg?.departureAirport || leg?.origin || "";
      const arrAirport = leg?.arrAirport || leg?.arrivalAirport || leg?.destination || "";
      const airlineName = leg?.airlineName || leg?.carrierName || f?.airlineName || "";
      const airlineLogo = leg?.airlineLogo || leg?.carrierLogo || f?.airlineLogo || f?.logo || "";
      const supplierCode = leg?.supplierCode || leg?.supplier || f?.supplierCode || f?.supplier || "";

      const flightCode = carrier && flightNum ? `${carrier}${flightNum}` : flightNum || carrier || "";
      const route = depAirport && arrAirport ? `${depAirport}-${arrAirport}` : "";
      const dateTime = depDate && depTime ? `${depTime} ${depDate}` : depDate || "";

      if (!flightCode && !dateTime) return null;

      const flightObj = {
        carrier,
        flightNumber: flightNum,
        flightCode,
        depDate,
        depTime,
        arrTime,
        arrDate,
        dateTime,
        route,
        depCode: depAirport ? String(depAirport).trim() : "",
        arrCode: arrAirport ? String(arrAirport).trim() : "",
        airlineName,
        airlineLogo,
        supplierCode: supplierCode ? String(supplierCode).trim() : "",
      };

      return enrichFlightWithAirlineInfo(flightObj, airlinesMap);
    }).filter(Boolean);
    
    if (flightObjects.length > 0) {
      return flightObjects;
    }
  }
  
  // נסה מ-flight ישירות
  if (flightFromResult) {
    const flights = Array.isArray(flightFromResult) ? flightFromResult : [flightFromResult];
    const flightObjects = flights.map((f) => {
      const leg = f?.leg || f;
      const carrier =
        leg?.carrier ||
        leg?.carrierCode ||
        leg?.airlineId ||
        leg?.airlineCode ||
        "";
      const flightNum = leg?.flightNumber || leg?.flight_number || "";
      const depDate = leg?.depDate || leg?.departureDate || leg?.departure?.date || "";
      const arrDate = leg?.arrDate || leg?.arrivalDate || leg?.arrival?.date || "";
      let depTime =
        leg?.depTime ||
        leg?.departureTime ||
        (leg?.departure?.time != null ? String(leg.departure.time) : "") ||
        "";
      let arrTime =
        leg?.arrTime ||
        leg?.arrivalTime ||
        (leg?.arrival?.time != null ? String(leg.arrival.time) : "") ||
        "";
      ({ depTime, arrTime } = normalizeSegmentClocks(depDate, depTime, arrDate, arrTime));
      const depAirport = leg?.depAirport || leg?.departureAirport || leg?.origin || "";
      const arrAirport = leg?.arrAirport || leg?.arrivalAirport || leg?.destination || "";
      const airlineName = leg?.airlineName || leg?.carrierName || f?.airlineName || "";
      const airlineLogo = leg?.airlineLogo || leg?.carrierLogo || f?.airlineLogo || f?.logo || "";
      const supplierCode = leg?.supplierCode || leg?.supplier || f?.supplierCode || f?.supplier || "";

      const flightCode = carrier && flightNum ? `${carrier}${flightNum}` : flightNum || carrier || "";
      const route = depAirport && arrAirport ? `${depAirport}-${arrAirport}` : "";
      const dateTime = depDate && depTime ? `${depTime} ${depDate}` : depDate || "";

      if (!flightCode && !dateTime) return null;

      const flightObj = {
        carrier,
        flightNumber: flightNum,
        flightCode,
        depDate,
        depTime,
        arrTime,
        arrDate,
        dateTime,
        route,
        depCode: depAirport ? String(depAirport).trim() : "",
        arrCode: arrAirport ? String(arrAirport).trim() : "",
        airlineName,
        airlineLogo,
        supplierCode: supplierCode ? String(supplierCode).trim() : "",
      };

      return enrichFlightWithAirlineInfo(flightObj, airlinesMap);
    }).filter(Boolean);
    
    if (flightObjects.length > 0) {
      return flightObjects;
    }
  }
  
  return null;
}

const PackageOrderSummary = ({
  allowSuccessHeader = true,
  /** When true (e.g. /packages/profile/:id summary-only): scroll window to top after order loads */
  resetScrollOnReady = false,
  /** מסך אישור ייעודי אחרי רישום העברה בנקאית ב־/packages/profile/:id/success */
  bankTransferSubmittedReceipt = false,
  /** `/p/dashboard/success/:orderId` — UX ייעודי לסוכן אחרי העברה בנקאית */
  agentDashboardBankTransferSuccess = false,
  /** פותח את מסך בחירת התשלום הקיים (packages/profile checkout flow) */
  onOpenPaymentSelection = null,
  /** הצעת לקוח (?offer=1): מעבר למילוי פרטי נוסעים לפני תשלום */
  onContinueToPassengerFlow = null,
} = {}) => {
  const { t } = useTranslation();
  const { id: routeOrderId, currentOrderId: dashboardSuccessOrderId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const {
    ourPrice,
    selectedResult,
    ilsToUsd,
    ourSavedLDSOrder,
    ldsSupplement,
    roomsAndPaxInfo,
  } = useSelectedResult();
  /** B2C חבילות: אין :id בנתיב — מזהה ההזמנה מ־context אחרי create/update LDS */
  const resolvedOrderId = useMemo(
    () => routeOrderId || dashboardSuccessOrderId || ourSavedLDSOrder?.id || null,
    [routeOrderId, dashboardSuccessOrderId, ourSavedLDSOrder?.id]
  );
  const lastQuoteLogOrderId = useRef(null);
  const [airlinesMap, setAirlinesMap] = useState(null);
  const [resolvedHotelImage, setResolvedHotelImage] = useState(null);

  const summaryRenderSeqRef = useRef(0);
  const lastSummaryRenderSnapRef = useRef(null);
  const lastBaggageDiagSnapRef = useRef(null);
  const lastBoardStatusDiagSnapRef = useRef(null);

  const routeMatched = useMemo(
    () => /^\/packages\/profile\/[^/]+\/success\/?$/.test(location.pathname || ""),
    [location.pathname]
  );

  const isAgentDashboardBankTransferSuccessUx = useMemo(() => {
    if (agentDashboardBankTransferSuccess) return true;
    return /^\/p\/dashboard\/success\/[^/]+\/?$/.test(location.pathname || "");
  }, [agentDashboardBankTransferSuccess, location.pathname]);

  const baggageDetails = useDeepCompareMemo(
    () =>
      order && typeof order === "object"
        ? resolveBaggageSummary(order, selectedResult, roomsAndPaxInfo, ldsSupplement)
        : null,
    [order, selectedResult, roomsAndPaxInfo, ldsSupplement]
  );

  const persistedDisplay = useDeepCompareMemo(
    () => resolvePersistedPublicOfferPackageIls(order),
    [order]
  );

  const boardResolved = useDeepCompareMemo(
    () =>
      order && typeof order === "object"
        ? resolveBoardBasisForPackageSummary(
            order,
            selectedResult,
            order?.passengersinfo?.[0]?.room || null
          )
        : null,
    [order, selectedResult, order?.passengersinfo]
  );

  const resolvedOrderStatus = useDeepCompareMemo(
    () =>
      order && typeof order === "object"
        ? resolveOrderPaymentStatusLabel(order, null, selectedResult)
        : null,
    [order, selectedResult]
  );

  const hotelImageSyncPick = useDeepCompareMemo(() => {
    if (!order || typeof order !== "object") return null;
    if (orderDealtypeIsOrganizedTour(order)) {
      return {
        kind: "org",
        ...resolveOrganizedTourSuccessHeroImage(order, selectedResult),
      };
    }
    return {
      kind: "pkg",
      ...resolveHotelImageFromProfileData(order, selectedResult),
    };
  }, [order, selectedResult]);

  const organizedTourSuccessModel = useDeepCompareMemo(() => {
    if (!order || typeof order !== "object") return null;
    if (!orderDealtypeIsOrganizedTour(order)) return null;
    const resH = order?.order_response?.reservation?.hotel ?? null;
    return buildOrganizedTourPackageSuccessViewModel(order, selectedResult, resH);
  }, [order, selectedResult]);

  const customerQuoteOffer = useMemo(
    () =>
      Boolean(
        order &&
          searchParams.get("offer") === "1" &&
          !isLdsOrderBookingConfirmed(order)
      ),
    [searchParams, order]
  );

  const customerQuoteAdditionalInfoHtml = useDeepCompareMemo(
    () => resolveCustomerQuoteAdditionalInfoHtml(order, selectedResult),
    [order, selectedResult]
  );

  const quoteResolved = useDeepCompareMemo(
    () =>
      customerQuoteOffer && order
        ? resolveCustomerQuoteUsdAndOfferFromOrder(
            mergeLdsSelectedResultForOfferDisplay(order, selectedResult),
            {
              ilsToUsd,
              contextOurPrice: ourPrice,
            }
          )
        : null,
    [customerQuoteOffer, order, selectedResult, ilsToUsd, ourPrice]
  );

  const successPayableLine = useDeepCompareMemo(
    () =>
      order && baggageDetails
        ? computePublicPackagesProfileSuccessPayableTotal({
            dedicatedSuccessRoute: routeMatched,
            order,
            persistedDisplay,
            baggageDetails,
          })
        : null,
    [routeMatched, order, persistedDisplay, baggageDetails]
  );

  const profileSuccessHeadlineIlsRaw = successPayableLine?.ils;
  const profileSuccessHeadlineIls = useMemo(() => {
    if (
      routeMatched &&
      profileSuccessHeadlineIlsRaw != null &&
      Number.isFinite(Number(profileSuccessHeadlineIlsRaw))
    ) {
      return Number(profileSuccessHeadlineIlsRaw);
    }
    return persistedDisplay?.ils ?? null;
  }, [routeMatched, profileSuccessHeadlineIlsRaw, persistedDisplay?.ils]);

  const flightDetails = useDeepCompareMemo(
    () =>
      order && typeof order === "object"
        ? extractFlightDetails(order, selectedResult, airlinesMap)
        : null,
    [order, selectedResult, airlinesMap]
  );

  summaryRenderSeqRef.current += 1;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: log every commit, compare snapshot inside
  useLayoutEffect(() => {
    const snap = {
      orderId: resolvedOrderId,
      loading,
      error: !!error,
      hasOrder: !!order,
      baggageKey: baggageDetails
        ? `${baggageDetails.hasBaggage}|${baggageDetails.totalBaggagePrice}|${baggageDetails.checkedBagsCount}|${baggageDetails.trolleyCount}`
        : null,
      boardSource: boardResolved?.source ?? null,
    };
    const prev = lastSummaryRenderSnapRef.current;
    const changedKeys = prev
      ? Object.keys(snap).filter((k) => !isEqual(snap[k], prev[k]))
      : Object.keys(snap);
    lastSummaryRenderSnapRef.current = snap;
    if (changedKeys.length > 0) {
      safeOrderSummaryTrace(
        "[ORDER_SUMMARY_RENDER_TRACE]",
        {
          renderCount: summaryRenderSeqRef.current,
          changedKeys,
          ...snap,
        },
        { dedup: { changedKeys, ...snap } }
      );
    }
  });

  // טעינת רשימת חברות תעופה
  useEffect(() => {
    loadAirlines()
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((airline) => {
            if (airline.code) {
              map[airline.code] = {
                name: airline.name || '',
                logo: airline.logo || '',
              };
            }
          });
          setAirlinesMap((prev) => (isEqual(prev, map) ? prev : map));
        }
      })
      .catch((err) => {
        console.warn("[PackageOrderSummary] Failed to load airlines:", err);
      });
  }, []);

  useLayoutEffect(() => {
    if (!resetScrollOnReady || loading) return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const inner = document.querySelector(".packages-profile-inner--summary-only");
    if (inner) inner.scrollTop = 0;
  }, [resetScrollOnReady, loading]);

  useEffect(() => {
    if (DEBUG_SUMMARY) {
      console.log("[PackageOrderSummary] mount/effect", {
        routeOrderId,
        contextOrderId: ourSavedLDSOrder?.id,
        resolvedOrderId,
        hasId: !!resolvedOrderId,
      });
    }
    if (!resolvedOrderId) {
      if (DEBUG_SUMMARY) {
        console.log(
          "[PackageOrderSummary] no order id (route or ourSavedLDSOrder) → error"
        );
      }
      setError((prev) => (prev === true ? prev : true));
      setLoading((prev) => (prev === false ? prev : false));
      return;
    }

    setError((prev) => (prev === false ? prev : false));
    setLoading((prev) => (prev === true ? prev : true));

    getLdsOrder({ id: resolvedOrderId })
      .then((res) => {
        if (DEBUG_SUMMARY) {
          console.log("[PackageOrderSummary] getLdsOrder success", {
            hasData: !!(res?.data),
            hasOrderResponse: !!(res?.data?.order_response),
            hasReservation: !!(res?.data?.order_response?.reservation),
          });
        }
        if (!res || !res.data) {
          console.error("Error: Invalid response from server");
          setError((p) => (p === true ? p : true));
          setLoading((p) => (p === false ? p : false));
          return;
        }

        setOrder((prev) => {
          if (isEqual(prev, res.data)) {
            safeOrderSummaryTrace("[ORDER_SUMMARY_SETSTATE_TRACE]", {
              name: "order",
              skipped: true,
              orderId: res.data?.id ?? null,
            });
            return prev;
          }
          safeOrderSummaryTrace("[ORDER_SUMMARY_SETSTATE_TRACE]", {
            name: "order",
            skipped: false,
            orderId: res.data?.id ?? null,
          });
          return res.data;
        });
        setLoading((p) => (p === false ? p : false));
        // Do NOT set error when order_response is missing: draft/saved orders and bank_transfer pending have no reservation yet
        const opStatus = res.data?.order_response?.opStatus;
        if (opStatus && opStatus.status === "INVALID_PARAM") {
          if (DEBUG_SUMMARY) console.log("[PackageOrderSummary] INVALID_PARAM from API → set error");
          setError((p) => (p === true ? p : true));
        }
      })
      .catch((err) => {
        if (DEBUG_SUMMARY) console.log("[PackageOrderSummary] getLdsOrder catch", err?.message);
        console.error("Error fetching order: ", err);
        setError((p) => (p === true ? p : true));
        setLoading((p) => (p === false ? p : false));
        if (err?.response?.data) {
          const errPayload = { error: err.response.data };
          setOrder((prev) => (isEqual(prev, errPayload) ? prev : errPayload));
        }
      });
  }, [resolvedOrderId]);

  useEffect(() => {
    if (!order) return;

    if (orderDealtypeIsOrganizedTour(order)) {
      const pick = hotelImageSyncPick;
      const has_placeholder = !pick?.url;
      if (DEBUG_SUMMARY) {
        console.info("[ORG_TOUR_SUCCESS_IMAGE_TRACE]", {
          order_id: order.id ?? resolvedOrderId,
          dealtype: order?.dealtype ?? order?.dealType ?? order?.deal_type ?? "OrganizedTour",
          image_url: pick?.url ?? null,
          image_source: pick?.source ?? null,
          has_placeholder,
        });
      }
      const u = pick?.url ?? null;
      setResolvedHotelImage((prev) => (prev === u ? prev : u));
      return;
    }

    const isCustomerOfferMode = searchParams.get("offer") === "1";
    const resolved = hotelImageSyncPick;
    if (DEBUG_SUMMARY) {
      console.warn("[PROFILE_PENDING_OFFER_IMAGE_SOURCE]", {
        orderId: resolvedOrderId,
        source: resolved?.source,
        candidate: resolved?.url || null,
      });
      if (isCustomerOfferMode) {
        console.warn("[PROFILE_OFFER_IMAGE_SOURCE]", {
          orderId: resolvedOrderId,
          source: resolved?.source,
          candidate: resolved?.url || null,
        });
      }
    }
    if (resolved?.url) {
      setResolvedHotelImage((prev) => (prev === resolved.url ? prev : resolved.url));
      if (DEBUG_SUMMARY) {
        console.warn("[PROFILE_HOTEL_IMAGE_RESOLVED]", {
          orderId: resolvedOrderId,
          source: resolved.source,
          url: resolved.url,
        });
      }
      return;
    }

    const hint = decodeHotelSearchHintFromOrder(order);
    if (!hint || (!hint.hotel_name && !hint.city_code && hint.hotel_id == null)) {
      setResolvedHotelImage((prev) => (prev === null ? prev : null));
      if (DEBUG_SUMMARY) {
        console.warn("[PROFILE_HOTEL_IMAGE_MISSING]", {
          orderId: resolvedOrderId,
          reason: "no_image_fields_and_no_hotel_hint_for_getHotelInfo",
        });
      }
      return;
    }

    let cancelled = false;
    getHotelInfo({
      hotel_id: hint.hotel_id ?? undefined,
      hotel_name: hint.hotel_name ?? undefined,
      city_code: hint.city_code ?? undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const img = extractFirstImageCandidate(res?.data || {});
        if (img) {
          setResolvedHotelImage((prev) => (prev === img ? prev : img));
          if (DEBUG_SUMMARY) {
            console.warn("[PROFILE_HOTEL_IMAGE_RESOLVED]", {
              orderId: resolvedOrderId,
              source: "getHotelInfoFallback",
              url: img,
            });
          }
          return;
        }
        setResolvedHotelImage((prev) => (prev === null ? prev : null));
        if (DEBUG_SUMMARY) {
          console.warn("[PROFILE_HOTEL_IMAGE_MISSING]", {
            orderId: resolvedOrderId,
            reason: "getHotelInfo_returned_without_images",
            hotelHint: hint,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setResolvedHotelImage((prev) => (prev === null ? prev : null));
        if (DEBUG_SUMMARY) {
          console.warn("[PROFILE_HOTEL_IMAGE_MISSING]", {
            orderId: resolvedOrderId,
            reason: "getHotelInfo_error",
            error: err?.message || String(err),
            hotelHint: hint,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [order, resolvedOrderId, searchParams, hotelImageSyncPick]);

  useEffect(() => {
    if (!order?.id || searchParams.get("offer") !== "1") return;
    if (isLdsOrderBookingConfirmed(order)) return;
    if (lastQuoteLogOrderId.current === order.id) return;
    lastQuoteLogOrderId.current = order.id;
    const qr = resolveCustomerQuoteUsdAndOfferFromOrder(
      mergeLdsSelectedResultForOfferDisplay(order, selectedResult),
      {
        ilsToUsd,
        contextOurPrice: ourPrice,
      }
    );
    console.log("[PackageOrderSummary] טעינת הצעת לקוח (?offer=1):", {
      orderId: resolvedOrderId,
      usd: qr?.usd,
      usdSource: qr?.usdSource,
      offerNumber: qr?.offerNumber,
      raw_customer_quote_display: order?.customer_quote_display,
      raw_ourprice_customerQuoteDisplay:
        order?.ourprice?.customerQuoteDisplay ?? order?.ourPrice?.customerQuoteDisplay,
    });
  }, [order, selectedResult, resolvedOrderId, searchParams, ilsToUsd, ourPrice]);

  useEffect(() => {
    if (!order || !baggageDetails) return;
    const nextSnap = {
      orderId: resolvedOrderId,
      baggageDetails,
      supplementKeys: ldsSupplement ? Object.keys(ldsSupplement).length : 0,
    };
    if (isEqual(lastBaggageDiagSnapRef.current, nextSnap)) return;
    lastBaggageDiagSnapRef.current = nextSnap;

    safeOrderSummaryTrace("[SUMMARY_BAGGAGE_INPUT_STATE]", {
      orderId: resolvedOrderId,
      selectedSupplementsKeys: ldsSupplement ? Object.keys(ldsSupplement).length : 0,
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_RAW]", {
      orderId: resolvedOrderId,
      orderSupplementType: typeof order?.supplement,
      orderSupplementCount: Array.isArray(order?.supplement)
        ? order.supplement.length
        : order?.supplement && typeof order.supplement === "object"
          ? Object.keys(order.supplement).length
          : 0,
      packageSupplementType: typeof selectedResult?.supplement,
      packageSupplementCount: Array.isArray(selectedResult?.supplement)
        ? selectedResult.supplement.length
        : selectedResult?.supplement && typeof selectedResult.supplement === "object"
          ? Object.keys(selectedResult.supplement).length
          : 0,
      baggageFee: order?.BaggageFee ?? order?.baggageFee ?? null,
      ourFareSum: order?.OurFareSum ?? null,
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_RESOLVED]", {
      orderId: resolvedOrderId,
      hasBaggage: baggageDetails.hasBaggage,
      checkedBagsCount: baggageDetails.checkedBagsCount,
      trolleyCount: baggageDetails.trolleyCount,
      carryOnCount: baggageDetails.carryOnCount,
      totalBaggagePrice: baggageDetails.totalBaggagePrice,
      labelLines: baggageDetails.labelLines,
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_AVAILABLE]", {
      orderId: resolvedOrderId,
      availableBaggageOptionsCount: baggageDetails.availableBaggageOptionsCount ?? 0,
      availableCounts: baggageDetails.availableCounts ?? null,
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_SELECTED_ONLY]", {
      orderId: resolvedOrderId,
      selectedBaggageItemsCount: baggageDetails.selectedBaggageItemsCount ?? 0,
      checkedBagsCount: baggageDetails.checkedBagsCount,
      trolleyCount: baggageDetails.trolleyCount,
      carryOnCount: baggageDetails.carryOnCount,
      labelLines: baggageDetails.labelLines,
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_SELECTED_BAGGAGE_RAW]", {
      orderId: resolvedOrderId,
      selectedBaggageItemsCount: baggageDetails.selectedBaggageItemsCount ?? 0,
      selectedPaidLines: baggageDetails.selectedPaidLines ?? [],
      includedLines: baggageDetails.includedLines ?? [],
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_SELECTED_BAGGAGE_LINES]", {
      orderId: resolvedOrderId,
      lines: baggageDetails.selectedPaidLines ?? [],
      includedLines: baggageDetails.includedLines ?? [],
    });
    safeOrderSummaryTrace("[SUMMARY_BAGGAGE_RESOLVED_LINES]", {
      orderId: resolvedOrderId,
      lines: baggageDetails.labelLines ?? [],
    });
    safeOrderSummaryTrace("[ORDER_SUMMARY_SELECTED_BAGGAGE_TOTAL]", {
      orderId: resolvedOrderId,
      selectedPaidTotal: baggageDetails.selectedPaidTotal ?? 0,
      totalBaggagePrice: baggageDetails.totalBaggagePrice ?? 0,
    });
    if (!baggageDetails.hasBaggage && baggageDetails.totalBaggagePrice > 0) {
      safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_MISSING_BUT_PRICE_CHANGED]", {
        orderId: resolvedOrderId,
        totalBaggagePrice: baggageDetails.totalBaggagePrice,
        reason: "price_contains_baggage_but_no_classified_items",
      });
    }
    safeOrderSummaryTrace("[SUMMARY_BAGGAGE_RENDER_DECISION]", {
      orderId: resolvedOrderId,
      shouldRender: baggageDetails.hasBaggage,
      selectedPaidTotal: baggageDetails.selectedPaidTotal ?? 0,
    });
  }, [order, resolvedOrderId, baggageDetails, ldsSupplement, selectedResult]);

  useEffect(() => {
    if (!order || !boardResolved || !resolvedOrderStatus) return;
    const proposalMode = !isLdsOrderBookingConfirmed(order);
    const nextSnap = {
      orderId: resolvedOrderId,
      proposalMode,
      boardResolved,
      resolvedOrderStatus,
    };
    if (isEqual(lastBoardStatusDiagSnapRef.current, nextSnap)) return;
    lastBoardStatusDiagSnapRef.current = nextSnap;

    safeOrderSummaryTrace("[ORDER_SUMMARY_STATUS_AGENT_SOURCE]", {
      orderId: resolvedOrderId,
      source: resolvedOrderStatus.source,
      raw: resolvedOrderStatus.raw,
      mode: proposalMode ? "proposal" : "confirmed",
    });
    if (resolvedOrderStatus.hasStatus) {
      safeOrderSummaryTrace("[ORDER_SUMMARY_STATUS_RESOLVED]", {
        orderId: resolvedOrderId,
        source: resolvedOrderStatus.source,
        label: resolvedOrderStatus.label,
        mode: proposalMode ? "proposal" : "confirmed",
      });
    } else {
      safeOrderSummaryTrace("[ORDER_SUMMARY_STATUS_MISSING]", {
        orderId: resolvedOrderId,
        source: resolvedOrderStatus.source,
        mode: proposalMode ? "proposal" : "confirmed",
      });
    }
    safeOrderSummaryTrace("[ORDER_SUMMARY_BOARD_AGENT_SOURCE]", {
      orderId: resolvedOrderId,
      source: boardResolved.source,
      raw: boardResolved.raw,
      mode: proposalMode ? "proposal" : "confirmed",
    });
    if (boardResolved.hasBoard) {
      safeOrderSummaryTrace("[ORDER_SUMMARY_BOARD_RESOLVED]", {
        orderId: resolvedOrderId,
        source: boardResolved.source,
        boardCode: boardResolved.boardCode,
        boardLabel: boardResolved.boardLabel,
        mode: proposalMode ? "proposal" : "confirmed",
      });
    } else {
      safeOrderSummaryTrace("[ORDER_SUMMARY_BOARD_MISSING]", {
        orderId: resolvedOrderId,
        source: boardResolved.source,
        mode: proposalMode ? "proposal" : "confirmed",
      });
    }
  }, [order, resolvedOrderId, boardResolved, resolvedOrderStatus]);

  const customerQuoteGreetingName = useMemo(() => {
    if (searchParams.get("offer") !== "1" || !order) return null;
    if (isLdsOrderBookingConfirmed(order)) return null;
    const n = resolveProposalCustomerNameForOffer({
      orderId: resolvedOrderId,
      searchParams,
      order,
    });
    return n || null;
  }, [resolvedOrderId, searchParams, order]);

  useEffect(() => {
    if (!order || !routeMatched || !organizedTourSuccessModel) return;
    const imgPick = resolveOrganizedTourSuccessHeroImage(order, selectedResult);
    const image_source = resolvedHotelImage
      ? imgPick.url && resolvedHotelImage === imgPick.url
        ? imgPick.source
        : "live_or_getHotelInfo_fallback"
      : imgPick.source;
    const bank_transfer = !!(
      order.bank_transfer === true ||
      order.bank_transfer === "true" ||
      order.later === true ||
      order.later === "true"
    );
    const status_source =
      bank_transfer && bankTransferSubmittedReceipt
        ? "organized_tour_bank_transfer_copy"
        : "default_flow";
    // eslint-disable-next-line no-console
    console.info("[ORG_TOUR_SUCCESS_PAGE_RENDER_TRACE]", {
      order_id: resolvedOrderId,
      dealtype: "OrganizedTour",
      image_source,
      title_source: organizedTourSuccessModel.titleSource,
      status_source,
      bank_transfer,
    });
  }, [
    order,
    routeMatched,
    organizedTourSuccessModel,
    resolvedOrderId,
    resolvedHotelImage,
    bankTransferSubmittedReceipt,
  ]);

  useEffect(() => {
    if (!order) return;
    const op = order?.ourprice ?? order?.ourPrice ?? {};
    const resolvedDisplayTotal =
      persistedDisplay?.ils != null && Number.isFinite(Number(persistedDisplay.ils))
        ? Number(persistedDisplay.ils)
        : null;
    const baggagePrice = op?.baggagePrice ?? op?.baggage_price ?? null;
    const creditCardFee = op?.creditCardFee ?? op?.credit_card_fee ?? null;
    const priceWithCommissionAndCreditCardFee =
      op?.priceWithCommissionAndCreditCardFee ??
      op?.price_with_commission_and_credit_card_fee ??
      null;
    const payload = {
      orderId: resolvedOrderId,
      sumpaid: order?.sumpaid ?? null,
      baggagePrice,
      priceWithCommissionAndCreditCardFee,
      priceWithCreditCardFee:
        op?.priceWithCreditCardFee ?? op?.price_with_credit_card_fee ?? null,
      priceWithHandleFee: op?.priceWithHandleFee ?? op?.price_with_handle_fee ?? null,
      creditCardFee,
      resolvedDisplayTotal,
      routeMatched,
    };
    safeOrderSummaryTrace("[LDS_SUCCESS_TOTAL_DEBUG]", payload, {
      dedup: buildLdsSuccessTotalDebugDedup({
        sumpaid: order?.sumpaid ?? null,
        total: resolvedDisplayTotal,
        baggagePrice,
        creditCardFee,
        priceWithCommissionAndCreditCardFee,
      }),
    });
  }, [order, persistedDisplay?.ils, resolvedOrderId, routeMatched]);

  if (error) {
    if (DEBUG_SUMMARY) console.log("[PackageOrderSummary] render error state", { hasOrder: !!order });
    let errMsg = "אירעה שגיאה בתהליך ההזמנה, אנא פנה לתמיכה";
    
    // Check if we have order data with error details
    if (order) {
      const opStatus = order?.order_response?.opStatus;
      if (opStatus && opStatus.status === "INVALID_PARAM") {
        errMsg = routeMatched
          ? "הבקשה נקלטה. לסיום והנחיות תשלום צרו קשר עם הסוכן."
          : opStatus.message || errMsg;
      } else if (order.error) {
        // If there's an error object in the order
        errMsg = typeof order.error === 'string' ? order.error : errMsg;
      }
    }
    
    return (
      <div style={{ 
        color: "red", 
        fontSize: 30, 
        padding: "2rem",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        alignItems: "center"
      }}>
        <div>{errMsg}</div>
        <button 
          onClick={() => window.location.href = "/"}
          style={{
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            backgroundColor: "#0258a4",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer"
          }}
        >
          חזרה לעמוד הבית
        </button>
      </div>
    );
  }
  if (loading) {
    if (DEBUG_SUMMARY) console.log("[PackageOrderSummary] render loading");
    return <Loader size="large" />;
  }

  // Check if order data exists
  if (!order) {
    return (
      <div style={{ 
        color: "red", 
        fontSize: 30, 
        padding: "2rem",
        textAlign: "center"
      }}>
        לא נמצאו נתוני הזמנה
      </div>
    );
  }

  const opStatus = order?.order_response?.opStatus;
  if (opStatus && opStatus.status === "INVALID_PARAM") {
    if (routeMatched) {
      return (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            fontSize: "1.125rem",
            lineHeight: 1.5,
          }}
        >
          הבקשה נקלטה. לסיום והנחיות תשלום צרו קשר עם הסוכן.
        </div>
      );
    }
    return <div>{opStatus.message || "אירעה שגיאה בתהליך ההזמנה"}</div>;
  }

  const proposalBanner = isLdsProposalEntity(order) ? (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message="נתוני הצעה (proposal)"
      description="השורה נטענה מ־lds_proposals; ייתכן שחלק מהשדות שונים או חסרים לעומת הזמנה רגילה."
    />
  ) : null;

  const customerQuoteAgencyBranding = customerQuoteOffer
    ? buildCustomerQuoteAgencyBranding(order)
    : null;

  const reservation = order?.order_response?.reservation;
  const resHotel = reservation?.hotel;

  // Proposal / draft / pre-payment: NOT a completed business confirmation from API.
  // (Having order_response.reservation alone is NOT enough — it may be a saved deal snapshot.)
  if (!isLdsOrderBookingConfirmed(order)) {
    if (DEBUG_SUMMARY) console.log("[PackageOrderSummary] not business-confirmed → proposal/draft summary");
    const { hotel: hotelFromResult } = selectedResult || {};
    const depFromSel = selectedResult ? get_depret_from_result(selectedResult) : {};
    const depRet = {
      depDate:
        depFromSel?.depDate ||
        resHotel?.checkInDate ||
        order?.hotel?.checkInDate ||
        order?.dep_date ||
        "-",
      retDate:
        depFromSel?.retDate ||
        resHotel?.checkOutDate ||
        order?.hotel?.checkOutDate ||
        order?.ret_date ||
        "-",
    };
    const totalPeopleCounts = (order?.passengersinfo && Array.isArray(order.passengersinfo))
      ? order.passengersinfo.reduce(
          (acc, curr) => {
            acc.totalAdults += (curr?.adults && Array.isArray(curr.adults)) ? curr.adults.length : 0;
            acc.totalKids += (curr?.kids && Array.isArray(curr.kids)) ? curr.kids.length : 0;
            return acc;
          },
          { totalAdults: 0, totalKids: 0 }
        )
      : { totalAdults: 0, totalKids: 0 };
    const firstRoom = order?.passengersinfo?.[0];
    const firstAdult = firstRoom?.adults?.[0];
    const orderFullName = firstAdult
      ? `${firstAdult?.paxTitle || ""} ${firstAdult?.firstName || ""} ${firstAdult?.lastName || ""}`.trim()
      : "-";
    let hotelCountryData = null;
    try {
      hotelCountryData = (hotelFromResult?.hotelCity || resHotel?.hotelCity || order?.hotel?.hotelCity)
        ? translateLDSDestinations(
            hotelFromResult?.hotelCity || resHotel?.hotelCity || order?.hotel?.hotelCity
          )
        : null;
    } catch (err) {
      hotelCountryData = null;
    }
    const hotelName =
      hotelFromResult?.hotelName || resHotel?.hotelName || order?.hotel?.hotelName || "-";
    const hotelCity =
      hotelCountryData?.CITYNAME_HEB ||
      hotelFromResult?.hotelCity ||
      resHotel?.hotelCity ||
      order?.hotel?.hotelCity ||
      "-";
    const statusLabel = customerQuoteOffer
      ? "הצעת מחיר"
      : resolvedOrderStatus.label;
    const draftStatusDisplay =
      bankTransferSubmittedReceipt && isBankTransferAwaitingPayment(order)
        ? t("packageBankTransferReceipt.statusBadge")
        : statusLabel;
    const orderTotalDisplay =
      profileSuccessHeadlineIls != null
        ? profileSuccessHeadlineIls
        : "מחיר לא זמין";
    if (DEBUG_SUMMARY) {
      console.log("[PackageOrderSummary] draft price source (persisted only)", {
        chosenSource: persistedDisplay.source,
        chosenField: persistedDisplay.fieldName,
        ilsValue: persistedDisplay.ils,
        display: orderTotalDisplay,
      });
    }
    const hotel = order?.hotel || hotelFromResult || resHotel;
    const board = boardResolved.hasBoard ? boardResolved.boardLabel : undefined;
    const roomsCountDraft = (order?.passengersinfo && Array.isArray(order.passengersinfo))
      ? order.passengersinfo.length
      : 1;
    const draftPaymentLink = order?.payment_links?.[0]?.url ?? order?.payment_link ?? null;
    const pendingOfferOrderId =
      order?.id != null && String(order.id).trim() !== ""
        ? String(order.id).trim()
        : resolvedOrderId != null && String(resolvedOrderId).trim() !== ""
          ? String(resolvedOrderId).trim()
          : null;
    const handlePendingOfferProceedToPayment = async () => {
      const paymentAmountIls =
        persistedDisplay.ils != null && Number.isFinite(Number(persistedDisplay.ils))
          ? Number(persistedDisplay.ils)
          : null;

      if (customerQuoteOffer && typeof onContinueToPassengerFlow === "function") {
        logPublicOfferContinueFlowTrace({
          source: "PackageOrderSummary.continue_button",
          offerMode: true,
          currentPath: `${location.pathname || ""}${location.search || ""}`,
          targetPath: `${location.pathname || ""}?checkout=1`,
          packageId: pendingOfferOrderId,
          hasPassengerData: false,
          hasSelectedRooms: !!(roomsAndPaxInfo && roomsAndPaxInfo.length > 0),
          totalPriceIls: paymentAmountIls,
          reason: "customer_quote_offer_to_passengers",
        });
        try {
          const handled = await onContinueToPassengerFlow({
            orderId: pendingOfferOrderId,
            amountIls: paymentAmountIls,
          });
          if (handled === true) return true;
          message.error("לא ניתן להמשיך למילוי פרטים. נסו שוב.");
          return false;
        } catch (err) {
          logPublicOfferContinueFlowTrace(
            {
              source: "PackageOrderSummary.continue_button",
              offerMode: true,
              packageId: pendingOfferOrderId,
              reason: "passenger_flow_exception",
              errorMessage: err?.message || String(err),
            },
            { force: true }
          );
          message.error("אירעה שגיאה במעבר למילוי פרטים. נסו שוב.");
          return false;
        }
      }

      console.warn("[PENDING_OFFER_PAYMENT_SELECTION_CONTEXT]", {
        orderId: pendingOfferOrderId,
        hasExternalSelectionHandler: typeof onOpenPaymentSelection === "function",
        currentBusinessStatus: deriveLdsOrderBusinessStatus(order),
        isBankTransferAwaiting: isBankTransferAwaitingPayment(order),
      });
      if (typeof onOpenPaymentSelection === "function") {
        console.warn("[PENDING_OFFER_PAYMENT_SELECTION_OPEN]", {
          orderId: pendingOfferOrderId,
          source: "pending_offer_summary_button",
        });
        try {
          onOpenPaymentSelection({
            orderId: pendingOfferOrderId,
            preferredMethod: isBankTransferAwaitingPayment(order)
              ? "bank_transfer"
              : "credit",
            amountIls:
              persistedDisplay.ils != null && Number.isFinite(Number(persistedDisplay.ils))
                ? Number(persistedDisplay.ils)
                : null,
          });
          return true;
        } catch (err) {
          const errMsg = err?.message || String(err);
          console.warn("[PENDING_OFFER_PAYMENT_ERROR]", {
            orderId: pendingOfferOrderId,
            reason: "open_payment_selection_exception",
            errorMessage: errMsg,
          });
          message.error("אירעה שגיאה בפתיחת מסך התשלום. נסו שוב.");
          return false;
        }
      }

      const existingPaymentUrl = pickFirstUsablePaymentUrl(order);
      console.warn("[PENDING_OFFER_PAYMENT_CONTEXT]", {
        orderId: pendingOfferOrderId,
        hasOrder: !!order,
        amountIls: paymentAmountIls,
        amountSource: persistedDisplay.source,
        amountField: persistedDisplay.fieldName,
        hasExistingPaymentUrl: !!existingPaymentUrl,
      });

      if (!pendingOfferOrderId) {
        const reason = "missing_order_id";
        console.warn("[PENDING_OFFER_PAYMENT_ERROR]", { reason });
        message.error("לא ניתן להמשיך לתשלום: מזהה ההזמנה חסר.");
        return false;
      }

      if (existingPaymentUrl) {
        console.warn("[PENDING_OFFER_PAYMENT_ROUTE_DECISION]", {
          orderId: pendingOfferOrderId,
          decision: "reuse_existing_payment_url",
          paymentUrl: existingPaymentUrl,
        });
        window.location.assign(existingPaymentUrl);
        return true;
      }

      if (paymentAmountIls == null || paymentAmountIls <= 0) {
        const reason = "missing_payment_amount";
        console.warn("[PENDING_OFFER_PAYMENT_ERROR]", {
          orderId: pendingOfferOrderId,
          reason,
          amountIls: paymentAmountIls,
        });
        message.error("לא ניתן להמשיך לתשלום: סכום ההזמנה חסר או לא תקין.");
        return false;
      }

      console.warn("[PENDING_OFFER_PAYMENT_ROUTE_DECISION]", {
        orderId: pendingOfferOrderId,
        decision: "create_lds_payment_url",
        amountIls: paymentAmountIls,
      });
      try {
        const paymentRes = await executeLdsCreditPaymentLink({
          orderId: pendingOfferOrderId,
          selectedResult,
          currentCard: {
            amount: paymentAmountIls,
            amountWithInterest: paymentAmountIls,
            numberofpayments: 1,
          },
          logContext: {
            flow: "pending_offer_continue_button",
            orderId: pendingOfferOrderId,
          },
        });
        const paymentUrl =
          paymentRes?.primaryPaymentUrl ??
          pickFirstUsablePaymentUrl(paymentRes?.order || null);
        if (paymentRes?.ok && paymentUrl) {
          console.warn("[PENDING_OFFER_PAYMENT_ROUTE_DECISION]", {
            orderId: pendingOfferOrderId,
            decision: "redirect_to_created_payment_url",
            paymentUrl,
          });
          window.location.assign(paymentUrl);
          return true;
        }
        const errMsg =
          paymentRes?.errorMessage ||
          "לא ניתן היה לייצר קישור תשלום להזמנה זו";
        console.warn("[PENDING_OFFER_PAYMENT_ERROR]", {
          orderId: pendingOfferOrderId,
          reason: "create_payment_link_failed",
          errorMessage: errMsg,
          status: paymentRes?.status ?? null,
        });
        message.error(errMsg);
        return false;
      } catch (err) {
        const errMsg = err?.message || String(err);
        console.warn("[PENDING_OFFER_PAYMENT_ERROR]", {
          orderId: pendingOfferOrderId,
          reason: "unexpected_exception",
          errorMessage: errMsg,
        });
        message.error("אירעה שגיאה בפתיחת התשלום. נסו שוב בעוד רגע.");
        return false;
      }
    };
    const draftHasCityTax = !!(order?.city_tax ?? order?.localTax ?? order?.cityTax ?? order?.hotel?.city_tax);
    const draftRoomType = order?.hotel?.roomType || selectedResult?.hotel?.roomType || undefined;
    const draftRoomNamesList = buildRoomNamesList(order, draftRoomType);
    const draftTotalUsdLegacy =
      toUsdNumber(order?.ourprice?.priceWithCreditCardFeeUsd) ??
      toUsdNumber(order?.ourprice?.priceInUsd) ??
      toUsdNumber(order?.ourprice?.usd) ??
      toUsdNumber(order?.ourprice?.totalUsd) ??
      toUsdNumber(order?.ourprice?.priceUsd) ??
      toUsdNumber(order?.totalUsd) ??
      toUsdNumber(order?.priceUsd) ??
      toUsdNumber(ourPrice?.priceWithCreditCardFeeUsd) ??
      toUsdNumber(ourPrice?.priceInUsd) ??
      toUsdNumber(ourPrice?.usd) ??
      toUsdNumber(ourPrice?.totalUsd) ??
      toUsdNumber(ourPrice?.priceUsd) ??
      toUsdNumber(selectedResult?.ourPrice?.ourTotalFare?.priceWithCreditCardFeeUsd) ??
      toUsdNumber(selectedResult?.ourPrice?.ourTotalFare?.priceInUsd) ??
      toUsdNumber(selectedResult?.ourPrice?.ourTotalFare?.totalUsd) ??
      toUsdNumber(selectedResult?.ourPrice?.priceWithCreditCardFeeUsd) ??
      toUsdNumber(selectedResult?.ourPrice?.priceInUsd);
    const draftTotalUsd = customerQuoteOffer
      ? quoteResolved?.usd ?? draftTotalUsdLegacy
      : draftTotalUsdLegacy;
    // חילוץ פרטי טיסה - מחזיר מערך של אובייקטים או null
    const flightDetails = extractFlightDetails(order, selectedResult, airlinesMap);
    const passengerCountForBaggage =
      (totalPeopleCounts.totalAdults || 0) + (totalPeopleCounts.totalKids || 0);
    const publicOfferIncludedBaggageVm = customerQuoteOffer
      ? buildPublicOfferIncludedBaggageVm({
          selectedResult,
          passengerCount: passengerCountForBaggage,
          packageId: pendingOfferOrderId ?? resolvedOrderId,
        })
      : null;
    const publicOfferIncludedTransfersVm = customerQuoteOffer
      ? buildPublicOfferIncludedTransfersVm({ selectedResult })
      : null;

    const ot = organizedTourSuccessModel;
    const draftDetailRows = ot
      ? [
          { title: "סטטוס ההזמנה", value: draftStatusDisplay },
          { title: "יעד", value: ot.destination },
          { title: "תאריך יציאה", value: ot.depDate },
          { title: "תאריך חזרה", value: ot.retDate },
          { title: "כניסה", value: ot.depDate },
          { title: "עזיבה", value: ot.retDate },
          { title: "מספר לילות", value: ot.nights },
          { title: "ימים (לפי תאריכים)", value: ot.daysLabel },
          { title: "סה״כ מבוגרים", value: 0 },
          { title: "סה״כ ילדים", value: 0 },
          ...(ot.supplierLabel ? [{ title: "ספק", value: ot.supplierLabel }] : []),
          { title: "עלות ההזמנה", value: orderTotalDisplay },
        ]
      : [
          {
            title: "סטטוס ההזמנה",
            value: draftStatusDisplay,
          },
          { title: "שם המלון", value: hotelName },
          { title: "מיקום המלון", value: hotelCity },
          { title: "סה״כ מבוגרים", value: totalPeopleCounts.totalAdults || 0 },
          { title: "סה״כ ילדים", value: totalPeopleCounts.totalKids || "-" },
          { title: "מספר לילות", value: order?.hotel?.nights || resHotel?.nights || "-" },
          { title: "כניסה", value: depRet.depDate || order?.hotel?.checkInDate || "-" },
          { title: "עזיבה", value: depRet.retDate || order?.hotel?.checkOutDate || "-" },
          { title: "עלות ההזמנה", value: orderTotalDisplay },
        ];
    
    const draftOrderProps = {
      loading: false,
      customerQuoteOffer,
      hotelCountry: ot ? ot.destination : hotelCity,
      hotelname: ot ? ot.heroTitle : hotelName,
      hotelMainImage: resolvedHotelImage,
      fullname: orderFullName || "-",
      orderReqData: order || {},
      rating: ot
        ? undefined
        : selectedResult?.hotel?.category
          ? String(selectedResult.hotel.category).replace(/\D/g, "").slice(0, 1) || undefined
          : undefined,
      roomType: draftRoomType,
      roomNamesList: draftRoomNamesList,
      board,
      passengersList: buildPassengersList(order),
      roomsCount: roomsCountDraft,
      paymentLink: draftPaymentLink,
      hasCityTax: ot ? false : draftHasCityTax,
      totalPriceUsd: draftTotalUsd,
      onProceedToPayment: handlePendingOfferProceedToPayment,
      pendingOfferPaymentContext: {
        orderId: pendingOfferOrderId,
        amountIls:
          persistedDisplay.ils != null && Number.isFinite(Number(persistedDisplay.ils))
            ? Number(persistedDisplay.ils)
            : null,
        hasPersistedPaymentLink: !!pickFirstUsablePaymentUrl(order),
      },
      customerQuoteOfferNumber: customerQuoteOffer
        ? quoteResolved?.offerNumber || null
        : null,
      baggageDetails,
      customerQuotePriceDisclaimer: customerQuoteOffer
        ? quoteResolved?.disclaimerHe || null
        : null,
      customerQuoteAdditionalInfoHtml,
      customerQuoteAgencyBranding,
      customerQuoteGreetingName,
      allowSuccessHeader: false,
      bankTransferSubmittedReceipt:
        !isAgentDashboardBankTransferSuccessUx &&
        bankTransferSubmittedReceipt &&
        isBankTransferAwaitingPayment(order),
      agentDashboardBankTransferSuccess:
        isAgentDashboardBankTransferSuccessUx && isBankTransferAwaitingPayment(order),
      showHeroStarRating: !ot,
      hideRoomDetailsSection: !!(ot && !ot.hasRealHotel),
      packageFactsSectionTitle: ot ? "פרטי הטיול" : null,
      packageGuestsSingleLine: ot ? ot.passengerSummary : null,
      organizedTourPriceFootnote: !!ot,
      statusHeaderSubtitleOverride:
        ot && bankTransferSubmittedReceipt && isBankTransferAwaitingPayment(order)
          ? ORG_TOUR_BANK_TRANSFER_SUCCESS_SUBTITLE_HE
          : null,
      details: draftDetailRows,
      flightDetails: flightDetails, // מעביר את פרטי הטיסה כאובייקט נפרד
      publicOfferIncludedBaggageVm,
      publicOfferIncludedTransfersVm,
    };
    return (
      <>
        {proposalBanner}
        <OrderSummary
          {...draftOrderProps}
          includedAttractionsSlot={
            ot ? null : (
              <PackageProfileAttractionsUnifiedBlock variant="includedOnly" />
            )
          }
        />
      </>
    );
  }

  if (!reservation) {
    if (DEBUG_SUMMARY) {
      console.warn(
        "[PackageOrderSummary] confirmed order but missing reservation — minimal fallback"
      );
    }
    return (
      <>
        {proposalBanner}
        <div style={{ padding: "2rem", textAlign: "center" }}>
          פרטי האישור ייטענו בקרוב. אם הבעיה נמשכת, פנו לתמיכה.
        </div>
      </>
    );
  }

  const { hotel, pax } = reservation;

  const mainPassanger = isArray(pax) ? pax[0] : pax;
  const orderFullName = `${mainPassanger?.paxTitle || ""}  ${mainPassanger?.firstName || ""}  ${mainPassanger?.lastName || ""}  `;

  const totalPeopleCounts = (order?.passengersinfo && Array.isArray(order.passengersinfo)) 
    ? order.passengersinfo.reduce(
        (acc, curr) => {
          acc.totalAdults += (curr?.adults && Array.isArray(curr.adults)) ? curr.adults.length : 0;
          acc.totalKids += (curr?.kids && Array.isArray(curr.kids)) ? curr.kids.length : 0;
          return acc;
        },
        { totalAdults: 0, totalKids: 0 }
      )
    : { totalAdults: 0, totalKids: 0 };

  let hotelCountryData = null;
  try {
    hotelCountryData = hotel?.hotelCity ? translateLDSDestinations(hotel.hotelCity) : null;
  } catch (err) {
    console.error("Error translating destination: ", err);
    hotelCountryData = null;
  }
  
  const orderTotalDisplay =
    profileSuccessHeadlineIls != null
      ? profileSuccessHeadlineIls
      : "מחיר לא זמין";
  if (DEBUG_SUMMARY) {
    console.log("[PackageOrderSummary] confirmed order price source (persisted only)", {
      chosenSource: persistedDisplay.source,
      chosenField: persistedDisplay.fieldName,
      ilsValue: persistedDisplay.ils,
      display: orderTotalDisplay,
    });
  }
  const boardConfirmed = boardResolved?.hasBoard ? boardResolved.boardLabel : undefined;
  const roomsCountConfirmed = (order?.passengersinfo && Array.isArray(order.passengersinfo))
    ? order.passengersinfo.length
    : 1;
  const confirmedPaymentLink = order?.payment_links?.[0]?.url ?? order?.payment_link ?? null;
  const confirmedHasCityTax = !!(order?.city_tax ?? order?.localTax ?? order?.cityTax ?? hotel?.city_tax);
  const confirmedRoomType = hotel?.roomType || undefined;
  const confirmedRoomNamesList = buildRoomNamesList(order, confirmedRoomType);
  const confirmedTotalUsdLegacy =
    toUsdNumber(order?.ourprice?.priceWithCreditCardFeeUsd) ??
    toUsdNumber(order?.ourprice?.priceInUsd) ??
    toUsdNumber(order?.ourprice?.usd) ??
    toUsdNumber(order?.ourprice?.totalUsd) ??
    toUsdNumber(order?.ourprice?.priceUsd) ??
    toUsdNumber(order?.totalUsd) ??
    toUsdNumber(order?.priceUsd) ??
    toUsdNumber(ourPrice?.priceWithCreditCardFeeUsd) ??
    toUsdNumber(ourPrice?.priceInUsd) ??
    toUsdNumber(ourPrice?.usd) ??
    toUsdNumber(ourPrice?.totalUsd) ??
    toUsdNumber(ourPrice?.priceUsd);
  const confirmedTotalUsd = customerQuoteOffer
    ? quoteResolved?.usd ?? confirmedTotalUsdLegacy
    : confirmedTotalUsdLegacy;

  const otConfirmed = organizedTourSuccessModel;
  const confirmedDetailRows = otConfirmed
    ? [
        {
          title: "סטטוס ההזמנה",
          value: customerQuoteOffer ? "הצעת מחיר" : resolvedOrderStatus.label,
        },
        { title: "יעד", value: otConfirmed.destination },
        { title: "תאריך יציאה", value: otConfirmed.depDate },
        { title: "תאריך חזרה", value: otConfirmed.retDate },
        { title: "כניסה", value: otConfirmed.depDate },
        { title: "עזיבה", value: otConfirmed.retDate },
        { title: "מספר לילות", value: otConfirmed.nights },
        { title: "ימים (לפי תאריכים)", value: otConfirmed.daysLabel },
        { title: "סה״כ מבוגרים", value: 0 },
        { title: "סה״כ ילדים", value: 0 },
        ...(otConfirmed.supplierLabel
          ? [{ title: "ספק", value: otConfirmed.supplierLabel }]
          : []),
        { title: "עלות ההזמנה", value: orderTotalDisplay },
      ]
    : [
        {
          title: "סטטוס ההזמנה",
          value: customerQuoteOffer
            ? "הצעת מחיר"
            : resolvedOrderStatus.label,
        },
        { title: "שם המלון", value: hotel?.hotelName || "-" },
        { title: "מיקום המלון", value: hotel?.location || "-" },
        { title: "סה״כ מבוגרים", value: totalPeopleCounts.totalAdults || 0 },
        { title: "סה״כ ילדים", value: totalPeopleCounts.totalKids || "-" },
        { title: "מספר לילות", value: hotel?.nights || "-" },
        { title: "כניסה", value: hotel?.checkInDate || "-" },
        { title: "עזיבה", value: hotel?.checkOutDate || "-" },
        { title: "עלות ההזמנה", value: orderTotalDisplay },
      ];
  
  const orderProps = {
    loading,
    allowSuccessHeader,
    customerQuoteOffer,
    hotelCountry: otConfirmed
      ? otConfirmed.destination
      : hotelCountryData?.CITYNAME_HEB || hotel?.hotelCity || "-",
    hotelname: otConfirmed ? otConfirmed.heroTitle : hotel?.hotelName || "-",
    hotelMainImage: resolvedHotelImage,
    fullname: orderFullName.trim() || "-",
    orderReqData: order || {},
    rating: otConfirmed
      ? undefined
      : hotel?.category
        ? String(hotel.category).replace(/\D/g, "").slice(0, 1) || undefined
        : undefined,
    roomType: confirmedRoomType,
    roomNamesList: confirmedRoomNamesList,
    board: boardConfirmed,
    passengersList: buildPassengersList(order),
    roomsCount: roomsCountConfirmed,
    paymentLink: confirmedPaymentLink,
    hasCityTax: otConfirmed ? false : confirmedHasCityTax,
    totalPriceUsd: confirmedTotalUsd,
    customerQuoteOfferNumber: customerQuoteOffer
      ? quoteResolved?.offerNumber || null
      : null,
    baggageDetails,
    customerQuotePriceDisclaimer: customerQuoteOffer
      ? quoteResolved?.disclaimerHe || null
      : null,
    customerQuoteAdditionalInfoHtml,
    customerQuoteAgencyBranding,
    customerQuoteGreetingName,
    agentDashboardBankTransferSuccess:
      isAgentDashboardBankTransferSuccessUx && isBankTransferAwaitingPayment(order),
    showHeroStarRating: !otConfirmed,
    hideRoomDetailsSection: !!(otConfirmed && !otConfirmed.hasRealHotel),
    packageFactsSectionTitle: otConfirmed ? "פרטי הטיול" : null,
    packageGuestsSingleLine: otConfirmed ? otConfirmed.passengerSummary : null,
    organizedTourPriceFootnote: !!otConfirmed,
    statusHeaderSubtitleOverride:
      otConfirmed && bankTransferSubmittedReceipt && isBankTransferAwaitingPayment(order)
        ? ORG_TOUR_BANK_TRANSFER_SUCCESS_SUBTITLE_HE
        : null,
    details: confirmedDetailRows,
    flightDetails: flightDetails, // מעביר את פרטי הטיסה כאובייקט נפרד
    publicOfferIncludedBaggageVm: customerQuoteOffer
      ? buildPublicOfferIncludedBaggageVm({
          selectedResult,
          passengerCount:
            (totalPeopleCounts.totalAdults || 0) + (totalPeopleCounts.totalKids || 0),
          packageId: pendingOfferOrderId ?? resolvedOrderId,
        })
      : null,
    publicOfferIncludedTransfersVm: customerQuoteOffer
      ? buildPublicOfferIncludedTransfersVm({ selectedResult })
      : null,
  };

  return (
    <>
      {proposalBanner}
      <OrderSummary
        {...orderProps}
        includedAttractionsSlot={
          otConfirmed ? null : (
            <PackageProfileAttractionsUnifiedBlock variant="includedOnly" />
          )
        }
      />
    </>
  );
};

export default PackageOrderSummary;
